<template>
  <div class="group-assignment-modal">
    <AppModal :close-on-overlay="!isMobile"
              :max-height="isMobile ? '85vh' : '80vh'"
              :show-close-button="true"
              :title="$t('general.select_assignment')"
              :visible.sync="isOpen"
              :width="isMobile ? 'calc(100vw - 32px)' : '720px'"
              @close="closeModal"
    >
      <div class="group-assignment-toolbar">
        <div class="group-assignment-toolbar-inner">
          <CompanySearchInput v-model="filter.search"
                              :placeholder="$t('general.search')"
                              class="group-assignment-search"
                              @input="debouncedSearch"
          />
          <sort-dropdown :sort-options="sortOptions"
                         @sort-selected="sortOrder"
          />
        </div>
      </div>
      <div class="group-assignment-body">
        <default-loading v-if="loading"></default-loading>
        <template v-else>
          <div v-if="userCount == 0" class="uk-alert-warning" uk-alert>
            <p>{{ $t("general.no_users_in_group") }}</p>
          </div>
          <table v-else-if="assignments.length != 0"
                 class="uk-table uk-table-middle uk-table-divider group-assignment-table"
          >
            <thead>
            <tr>
              <th>{{ $t("general.assignment_name") }}</th>
              <th>{{ $t("ajadam.end_date") }}</th>
              <th></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(assignment, index) in formattedItems" :key="index">
              <td :data-label="$t('general.assignment_name')">
                {{ assignment.name }}
              </td>
              <td :data-label="$t('ajadam.end_date')">
                {{ assignment.end_date }}
              </td>
              <td class="text-right group-assignment-action">
                <button v-if="$hasPermission('create_assignment')"
                        class="uk-button uk-button-default group-assignment-btn"
                        type="button"
                        @click="attachAssignment(assignment.id)"
                >
                  {{ $t("menu.company_dashboard.create_assignment") }}
                </button>
                <button v-else
                        class="uk-button uk-button-default group-assignment-btn"
                        disabled
                        type="button"
                >
                  {{ $t("menu.company_dashboard.create_assignment") }}
                </button>
              </td>
            </tr>
            </tbody>
          </table>
          <div v-else class="uk-alert-warning" uk-alert>
            <p>{{ $t("general.no_assignable_records") }}</p>
          </div>
          <pagination :pagination="paginationData"
                      class="mt-2 mb-3 group-assignment-pagination"
                      @changePage="setPage($event)"
          ></pagination>
        </template>
      </div>
      <template #footer>
        <button class="btn btn-danger" type="button" @click="closeModal">
          {{ $t("general.close") }}
        </button>
      </template>
    </AppModal>
  </div>
</template>

<script>
import store from "@/core/services";
import axios from "axios";
import moment from "moment";
import debounce from "lodash/debounce";
import Pagination from "@/assets/components/Pagination";
import DefaultLoading from "@/view/components/loading/DefaultLoading";
import CompanySearchInput from "@/view/company/components/search/CompanySearchInput.vue";
import SortDropdown from "@/view/components/dropdown/SortDropdown.vue";
import AppModal from "@/view/components/ui/AppModal.vue";
import assignment_module, {
  GET_ITEM as GET_ASSIGNMENT,
  ITEM as ASSIGNMENT_ITEM,
  MODULE_NAME as ASSIGNMENT_MODULE,
} from "@/core/services/store/assignment.module";

export default {
  name: "GroupAssignmentModal",
  components: {
    Pagination,
    DefaultLoading,
    CompanySearchInput,
    SortDropdown,
    AppModal,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    groupId: {
      type: [Number, String],
      default: null,
    },
    userCount: {
      type: [Number, String],
      default: null,
    },
  },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(ASSIGNMENT_MODULE, assignment_module);
  },
  data() {
    return {
      page: 1,
      per_page: 10,
      filter: {
        search: "",
        order_by: null,
        sort: null,
      },
      loading: false,
      isMobile: false,
    };
  },
  computed: {
    isOpen: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },
    sortOptions() {
      return [
        {
          key: "name-asc",
          sort: "name",
          order: "asc",
          label: this.$t("filters.order.a_z"),
        },
        {
          key: "name-desc",
          sort: "name",
          order: "desc",
          label: this.$t("filters.order.z_a"),
        },
        {
          key: "created_at-asc",
          sort: "created_at",
          order: "asc",
          label: this.$t("filters.order.created_at_asc"),
        },
        {
          key: "created_at-desc",
          sort: "created_at",
          order: "desc",
          label: this.$t("filters.order.created_at_desc"),
        },
      ];
    },
    assignments() {
      return store.getters[ASSIGNMENT_MODULE + "/" + ASSIGNMENT_ITEM];
    },
    paginationData() {
      if (
          this.assignments === null ||
          typeof this.assignments !== "object" ||
          !("data" in this.assignments)
      ) {
        return null;
      }
      return this.assignments;
    },
    formattedItems() {
      let results = [];
      if (this.assignments === null || !("data" in this.assignments)) return [];

      for (let i = 0; i < this.assignments.data.length; i++) {
        let currentItem = this.assignments.data[i];
        if (currentItem) {
          results.push({
            id: currentItem.id,
            name: currentItem.name,
            end_date: currentItem.end_date,
            status_id: currentItem.status,
            count: currentItem.count,
            created_at: currentItem.created_at
                ? moment(currentItem.created_at).format("DD/MM/YYYY HH:mm:ss")
                : "-",
          });
        }
      }
      return results;
    },
  },
  methods: {
    closeModal() {
      this.isOpen = false;
    },
    updateIsMobile() {
      this.isMobile = typeof window !== "undefined" && window.innerWidth <= 767;
    },
    searchAssignments() {
      this.page = 1;
      this.fetchAssignments();
    },
    sortOrder({ sort, order }) {
      this.page = 1;
      this.filter.order_by = sort;
      this.filter.sort = order;
      this.fetchAssignments();
    },
    setPage(page) {
      this.page = page;
      this.fetchAssignments();
    },
    attachAssignment(assignmentId) {
      if (!this.groupId) return;

      this.loading = true;
      axios
          .get(`/api/attach-group-assignment/${this.groupId}/${assignmentId}`, {
            headers: this.apiHeaders,
          })
          .then(() => {
            this.fetchAssignments();
          })
          .catch(() => {
            this.loading = false;
          });
    },
    fetchAssignments() {
      if (!this.groupId) return;

      this.loading = true;
      store
          .dispatch(ASSIGNMENT_MODULE + "/" + GET_ASSIGNMENT, {
            url: `api/group-assignments/${this.groupId}`,
            filters: {
              like: this.filter.search || null,
              page: this.page,
              per_page: this.per_page,
              order: this.filter.sort,
              sort: this.filter.order_by,
              isProvider: true,
            },
          })
          .finally(() => {
            this.loading = false;
          });
    },
    resetAndFetch() {
      this.page = 1;
      this.filter.search = "";
      this.fetchAssignments();
    },
  },
  watch: {
    visible(isOpen) {
      if (isOpen) {
        this.resetAndFetch();
      }
    },
  },
  created() {
    this.debouncedSearch = debounce(this.searchAssignments, 500);
  },
  mounted() {
    this.updateIsMobile();
    window.addEventListener("resize", this.updateIsMobile, { passive: true });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateIsMobile);
    if (this.debouncedSearch) {
      this.debouncedSearch.cancel();
    }
  },
};
</script>

<style scoped>
.group-assignment-modal {
  --groups-text: #1e293b;
  --groups-muted: #64748b;
  --groups-border: #e2e8f0;
}

.group-assignment-modal ::v-deep .app-modal-overlay {
  z-index: 1100;
}

.group-assignment-toolbar {
  margin-bottom: 16px;
}

.group-assignment-toolbar-inner {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.group-assignment-search {
  flex: 1 1 auto;
  min-width: 0;
}

.group-assignment-toolbar-inner ::v-deep .company-search-input,
.group-assignment-toolbar-inner ::v-deep .section-header-left {
  float: none;
  width: 100%;
  max-width: none;
  margin: 0;
}

.group-assignment-toolbar-inner ::v-deep .company-search-input input {
  height: auto;
  min-height: 0;
  padding: 10px 16px;
  font-size: 14px;
}

.group-assignment-toolbar-inner ::v-deep .sort-dropdown {
  flex: 0 0 auto;
  flex-shrink: 0;
}

.group-assignment-toolbar-inner ::v-deep .sort-dropdown .btn-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.group-assignment-body {
  min-width: 0;
}

.group-assignment-table th {
  text-transform: none !important;
}

.group-assignment-table tbody tr:nth-child(even) {
  background-color: #f2f2f2;
}

.group-assignment-table tbody tr:nth-child(odd) {
  background-color: #ffffff;
}

@media (max-width: 767px) {
  /* Mobilde tam ekran DEGIL: kenar bosluklu, ortalanmis diyalog */
  .group-assignment-modal ::v-deep .app-modal-overlay {
    padding: 16px;
    align-items: center;
    justify-content: center;
  }

  .group-assignment-modal ::v-deep .app-modal {
    /* yukseklik icerige gore; max-height prop'tan (85vh) geliyor */
    height: auto;
    min-height: 0;
    border-radius: 16px;
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.18);
  }

  .group-assignment-modal ::v-deep .app-modal-header {
    border-radius: 16px 16px 0 0;
  }

  .group-assignment-modal ::v-deep .app-modal-title {
    font-size: 16px;
    line-height: 1.4;
  }

  .group-assignment-modal ::v-deep .app-modal-close {
    min-width: 48px;
    min-height: 48px;
  }

  .group-assignment-modal ::v-deep .app-modal-body {
    flex: 1;
    min-height: 0;
    padding: 16px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .group-assignment-modal ::v-deep .app-modal-footer {
    padding: 16px;
  }

  .group-assignment-modal ::v-deep .app-modal-footer .btn {
    width: 100%;
    min-height: 48px;
    margin: 0;
    border-radius: 10px;
    font-size: 16px;
  }

  .group-assignment-modal ::v-deep .app-modal-footer .btn:active {
    transform: scale(0.98);
    opacity: 0.85;
  }

  .group-assignment-modal ::v-deep .sort-dropdown .uk-dropdown-nav > li > a {
    display: flex;
    align-items: center;
    min-height: 48px;
    padding: 12px 16px;
    font-size: 16px;
    line-height: 1.4;
  }

  .group-assignment-toolbar {
    margin-bottom: 16px;
  }

  .group-assignment-toolbar-inner {
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    gap: 8px;
  }

  .group-assignment-search {
    flex: 1 1 auto;
    min-width: 0;
    width: auto;
    margin-right: 0;
  }

  .group-assignment-toolbar-inner ::v-deep .company-search-input input {
    height: 48px;
    min-height: 48px;
    padding: 12px 16px;
    font-size: 16px;
  }

  .group-assignment-toolbar-inner ::v-deep .sort-dropdown {
    flex: 0 0 48px;
  }

  .group-assignment-toolbar-inner ::v-deep .sort-dropdown .btn-circle {
    width: 48px;
    height: 48px;
    min-width: 48px;
    min-height: 48px;
  }

  .group-assignment-body {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .group-assignment-table {
    min-width: 560px;
  }

  .group-assignment-table th,
  .group-assignment-table td {
    white-space: nowrap;
    font-size: 14px;
  }

  .group-assignment-btn {
    min-height: 44px;
    border-radius: 10px;
    font-size: 14px;
  }

  .group-assignment-btn:active {
    transform: scale(0.98);
    opacity: 0.85;
  }

  .group-assignment-btn:disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}

@media (hover: none) {
  .group-assignment-modal ::v-deep .app-modal-footer .btn:hover,
  .group-assignment-modal ::v-deep .app-modal-close:hover {
    opacity: 1;
    color: #666;
  }
}

@media (prefers-reduced-motion: reduce) {
  .group-assignment-btn:active,
  .group-assignment-modal ::v-deep .app-modal-footer .btn:active {
    transform: none;
  }
}
</style>
